import React, { createContext, useContext, useState, useEffect } from "react";
import { ref, onValue } from "firebase/database";
import { db } from "../firebase";

const AuctionContext = createContext();

export const useAuction = () => {
  const context = useContext(AuctionContext);
  if (!context) {
    throw new Error("useAuction must be used within AuctionProvider");
  }
  return context;
};

export const AuctionProvider = ({ children, gameId }) => {
  const [auctionStatus, setAuctionStatus] = useState("upcoming");
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [bids, setBids] = useState([]);

  useEffect(() => {
    if (!gameId) return;

    // Listen to auction status
    const statusRef = ref(db, `games/${gameId}/details/status`);
    const unsubscribeStatus = onValue(statusRef, (snapshot) => {
      if (snapshot.exists()) {
        setAuctionStatus(snapshot.val());
      }
    });

    // Listen to current player
    const currentPlayerRef = ref(db, `games/${gameId}/currentPlayer`);
    const unsubscribePlayer = onValue(currentPlayerRef, (snapshot) => {
      if (snapshot.exists()) {
        setCurrentPlayer(snapshot.val());
      } else {
        setCurrentPlayer(null);
      }
    });

    // Listen to teams
    const teamsRef = ref(db, `games/${gameId}/teams`);
    const unsubscribeTeams = onValue(teamsRef, (snapshot) => {
      if (snapshot.exists()) {
        const teamsData = snapshot.val();
        const teamsArray = Object.entries(teamsData).map(([id, team]) => ({
          ...team,
          id,
        }));
        setTeams(teamsArray);
      } else {
        setTeams([]);
      }
    });

    // Listen to players
    const playersRef = ref(db, `games/${gameId}/players`);
    const unsubscribePlayers = onValue(playersRef, (snapshot) => {
      if (snapshot.exists()) {
        const playersData = snapshot.val();
        const playersArray = Object.entries(playersData).map(([id, player]) => ({
          ...player,
          id,
        }));
        setPlayers(playersArray);
      } else {
        setPlayers([]);
      }
    });

    // Listen to bids
    const bidsRef = ref(db, `games/${gameId}/currentBids`);
    const unsubscribeBids = onValue(bidsRef, (snapshot) => {
      if (snapshot.exists()) {
        const bidsData = snapshot.val();
        const bidsArray = Object.entries(bidsData).map(([id, bid]) => ({
          ...bid,
          id,
        }));
        setBids(bidsArray);
      } else {
        setBids([]);
      }
    });

    return () => {
      unsubscribeStatus();
      unsubscribePlayer();
      unsubscribeTeams();
      unsubscribePlayers();
      unsubscribeBids();
    };
  }, [gameId]);

  const value = {
    auctionStatus,
    currentPlayer,
    teams,
    players,
    bids,
    gameId,
  };

  return (
    <AuctionContext.Provider value={value}>
      {children}
    </AuctionContext.Provider>
  );
};

export { AuctionContext };